import axios from 'axios'
import { createGenericStore } from '@/stores/utils/genericStore.js'

const ChecklistItemsStoreOptions = {
  state: () => ({
    checklistId: null,
    initializedChecklistIds: [],
    updatingCompletionItemIds: [],
    updatingNotesItemIds: [],
    addingChecklistItemIds: [],
    updatingInstructionItemIds: [],
    updatingOrderItemIds: [],
    updatingDescriptionItemIds: [],
    deletingChecklistItemIds: [],
  }),

  getters: {
    itemsForChecklist(state) {
      return (checklistId) => state.items.filter((item) => Number(item.checklist_id) === Number(checklistId))
    },
    isUpdatingCompletion(state) {
      return (itemId) => state.updatingCompletionItemIds.includes(itemId)
    },
    isUpdatingNotes(state) {
      return (itemId) => state.updatingNotesItemIds.includes(itemId)
    },
    isAddingChecklistItem(state) {
      return (checklistId) => state.addingChecklistItemIds.includes(Number(checklistId))
    },
    isUpdatingInstruction(state) {
      return (itemId) => state.updatingInstructionItemIds.includes(Number(itemId))
    },
    isUpdatingOrder(state) {
      return (itemId) => state.updatingOrderItemIds.includes(Number(itemId))
    },
    isUpdatingDescription(state) {
      return (itemId) => state.updatingDescriptionItemIds.includes(Number(itemId))
    },
    isDeleting(state) {
      return (itemId) => state.deletingChecklistItemIds.includes(Number(itemId))
    },
  },

  actions: {
    setChecklistContext(checklistId) {
      this.checklistId = Number(checklistId)
    },

    async fetchItemsForChecklist(checklistId, params = {}) {
      this.setChecklistContext(checklistId)

      return this.fetchItems({
        clear: true,
        not_paginated: true,
        checklist_id: this.checklistId,
        order_by: 'order',
        order_direction: 'asc',
        ...params,
      })
    },

    async fetchItemsForChecklistOnce(checklistId, params = {}) {
      const normalizedChecklistId = Number(checklistId)

      if (this.initializedChecklistIds.includes(normalizedChecklistId)) {
        return this.itemsForChecklist(normalizedChecklistId)
      }

      await this.fetchItemsForChecklist(normalizedChecklistId, params)
      this.initializedChecklistIds.push(normalizedChecklistId)

      return this.itemsForChecklist(normalizedChecklistId)
    },

    async updateCompletion(checklistItem, completed) {
      const checklistItemId = Number(checklistItem?.id)

      if (!checklistItemId) {
        return null
      }

      this.error = null
      this.updatingCompletionItemIds.push(checklistItemId)

      try {
        const { data } = await axios.patch(`${this.api}/${checklistItemId}`, {
          completed: Boolean(completed),
          completed_at: completed ? new Date().toISOString() : null,
          completed_by: completed ? checklistItem?.completed_by ?? null : null,
        })

        this.itemPatchState(data)

        return data
      } catch (error) {
        this.error = error
        throw error
      } finally {
        this.updatingCompletionItemIds = this.updatingCompletionItemIds.filter((id) => id !== checklistItemId)
      }
    },

    async updateNotes(checklistItemId, notes) {
      const normalizedChecklistItemId = Number(checklistItemId)

      if (!normalizedChecklistItemId) {
        return null
      }

      this.error = null
      this.updatingNotesItemIds.push(normalizedChecklistItemId)

      try {
        const { data } = await axios.patch(`${this.api}/${normalizedChecklistItemId}`, {
          notes,
        })

        this.itemPatchState(data)

        return data
      } catch (error) {
        this.error = error
        throw error
      } finally {
        this.updatingNotesItemIds = this.updatingNotesItemIds.filter((id) => id !== normalizedChecklistItemId)
      }
    },

    async createItemForChecklist(payload) {
      const normalizedChecklistId = Number(payload?.checklistId)
      const instruction = String(payload?.instruction ?? '').trim()
      const notes = String(payload?.notes ?? '').trim()

      if (normalizedChecklistId < 1 || instruction.length === 0) {
        throw new Error('A valid checklist and instruction are required.')
      }

      this.error = null
      this.addingChecklistItemIds.push(normalizedChecklistId)

      try {
        await this.fetchItemsForChecklistOnce(normalizedChecklistId)

        const existingChecklistItems = this.itemsForChecklist(normalizedChecklistId)
        const nextOrder = existingChecklistItems.reduce((maxOrder, checklistItem) => {
          const itemOrder = Number(checklistItem.order ?? -1)

          return Math.max(maxOrder, itemOrder)
        }, -1) + 1

        const { data: checklistItem } = await axios.post(this.api, {
          checklist_id: normalizedChecklistId,
          instruction,
          order: nextOrder,
          notes: notes || null,
          completed: false,
        })

        this.itemPatchState(checklistItem)

        return checklistItem
      } catch (error) {
        this.error = error
        throw error
      } finally {
        this.addingChecklistItemIds = this.addingChecklistItemIds.filter((id) => id !== normalizedChecklistId)
      }
    },

    async updateDescription(checklistItem, description) {
      const checklistItemId = Number(checklistItem?.id)

      if (!checklistItemId) {
        return null
      }

      const nextDescription = String(description ?? '').trim()
      const currentDescription = String(checklistItem?.details ?? '').trim()
      const shouldUpdateDescription = nextDescription !== currentDescription

      if (!shouldUpdateDescription) {
        return checklistItem
      }

      this.error = null
      this.updatingDescriptionItemIds.push(checklistItemId)

      try {
        const { data: updatedChecklistItem } = await axios.patch(`${this.api}/${checklistItemId}`, {
          details: nextDescription || null,
        })

        this.itemPatchState(updatedChecklistItem)

        return updatedChecklistItem
      } catch (error) {
        this.error = error
        throw error
      } finally {
        this.updatingDescriptionItemIds = this.updatingDescriptionItemIds.filter((id) => id !== checklistItemId)
      }
    },

    async updateInstruction(checklistItem, instruction) {
      const checklistItemId = Number(checklistItem?.id)

      if (!checklistItemId) {
        return null
      }

      const nextInstruction = String(instruction ?? '').trim()
      const currentInstruction = String(checklistItem?.instruction ?? '').trim()
      const shouldUpdateInstruction = nextInstruction !== currentInstruction

      if (!shouldUpdateInstruction) {
        return checklistItem
      }

      if (nextInstruction.length === 0) {
        throw new Error('A checklist item instruction is required.')
      }

      this.error = null
      this.updatingInstructionItemIds.push(checklistItemId)

      try {
        const { data: updatedChecklistItem } = await axios.patch(`${this.api}/${checklistItemId}`, {
          instruction: nextInstruction,
        })

        this.itemPatchState(updatedChecklistItem)

        return updatedChecklistItem
      } catch (error) {
        this.error = error
        throw error
      } finally {
        this.updatingInstructionItemIds = this.updatingInstructionItemIds.filter((id) => id !== checklistItemId)
      }
    },

    async updateOrder(checklistItem, order) {
      const checklistItemId = Number(checklistItem?.id)

      if (!checklistItemId) {
        return null
      }

      const nextOrder = Number(order)
      if (!Number.isInteger(nextOrder) || nextOrder < 0) {
        throw new Error('A valid checklist item order is required.')
      }

      const currentOrder = Number(checklistItem?.order ?? -1)
      if (currentOrder === nextOrder) {
        return checklistItem
      }

      this.error = null
      this.updatingOrderItemIds.push(checklistItemId)

      try {
        const { data: updatedChecklistItem } = await axios.patch(`${this.api}/${checklistItemId}`, {
          order: nextOrder,
        })

        this.itemPatchState(updatedChecklistItem)

        return updatedChecklistItem
      } catch (error) {
        this.error = error
        throw error
      } finally {
        this.updatingOrderItemIds = this.updatingOrderItemIds.filter((id) => id !== checklistItemId)
      }
    },

    async deleteItem(checklistItemId) {
      const normalizedChecklistItemId = Number(checklistItemId)

      if (normalizedChecklistItemId < 1) {
        return null
      }

      this.error = null
      this.deletingChecklistItemIds.push(normalizedChecklistItemId)

      try {
        await this.itemDelete(normalizedChecklistItemId)
      } catch (error) {
        this.error = error
        throw error
      } finally {
        this.deletingChecklistItemIds = this.deletingChecklistItemIds.filter((id) => id !== normalizedChecklistItemId)
      }

      return true
    },
  },
}

export const useChecklistItemsStore = createGenericStore(
  'checklistItems',
  '/api/v1/checklist-items',
  ChecklistItemsStoreOptions,
)
